import React, { useMemo } from 'react';
import { IActionItem } from '@components/ActionsMenu/types';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { ImageExport } from '@constants/entities/reports';
import { RolesPermissionsOrElements } from '@constants/entities/user';
import { useDashboard } from '@context/DashboardContext';
import { useProject } from '@context/Project/ProjectProvider';
import useCanvasZoom from '@hooks/canvas/useCanvasZoom';
import useRolesCheck from '@hooks/useRolesCheck';
import useScenarioMode from '@hooks/useScenarioMode';
import {
  ClipboardExportIcon,
  EyeOpenIcon,
  ReportExportIcon,
  ReportIcon,
} from '@utils/iconsDefs';
import { Tabs } from '@views/Insights/types';

type Options = {
  onOpenExportAsImage: () => void;
  onOpenExportLayersReport: () => void;
  onOpenExportFocusOnReport: () => void;
  onOpenExportRiskRegisterReport: () => void;
  onOpenExportAssetReport: () => void;
  onCopyToProject: () => void;
};

const useAdditionalMenuItems = ({
  onOpenExportAsImage,
  onOpenExportLayersReport,
  onOpenExportFocusOnReport,
  onOpenExportRiskRegisterReport,
  onOpenExportAssetReport,
  onCopyToProject,
}: Options) => {
  const dashboard = useDashboard();
  const { scenarioMode } = useScenarioMode();
  const { saveCurrentZoom } = useCanvasZoom();
  const { getActiveNode } = useStore();
  const activeNode = getActiveNode();

  const {
    toolbox: { mode },
    setIsUILoading,
    setIsVersionsShown,
    isDashboardMode,
    handleCloseDrawer,
    isModelMode,
    isRiskRegisterMode,
    insightsTab,
  } = useProject();

  const canCopyModel = useRolesCheck(RolesPermissionsOrElements.CopyModel);

  const handleViewVersionClick = async () => {
    await handleCloseDrawer();
    saveCurrentZoom();
    setIsUILoading(true);
    setIsVersionsShown(true);

    setTimeout(() => {
      setIsUILoading(false);
    }, 500);
  };

  const getReportOpenFn = () => {
    if (insightsTab === Tabs.FocusOn && !!activeNode) {
      return onOpenExportFocusOnReport;
    }

    if (isRiskRegisterMode) {
      return onOpenExportRiskRegisterReport;
    }

    if (mode === Modes.RiskManagement) {
      return onOpenExportAssetReport;
    }

    return onOpenExportLayersReport;
  };

  const dashboardExportItems = [
    ImageExport(() => dashboard.openExportModal(undefined)),
  ];

  const modelExportItems = [
    ...(isRiskRegisterMode
      ? []
      : [
          ImageExport(async () => {
            await handleCloseDrawer(undefined, false);
            onOpenExportAsImage();
          }),
        ]),

    {
      text: 'Report (.xlsx)',
      icon: <ReportIcon fill="currentColor" width={20} height={20} />,
      onClick: getReportOpenFn(),
    },
  ];

  return useMemo<IActionItem[]>(
    () => [
      ...(isModelMode && !scenarioMode && !isRiskRegisterMode
        ? [
            {
              text: 'Versions history',
              icon: <EyeOpenIcon fill="currentColor" />,
              onClick: handleViewVersionClick,
            },
          ]
        : []),

      {
        text: 'Export as',
        icon: <ReportExportIcon fill="currentColor" />,
        subItems: [
          ...(isDashboardMode ? dashboardExportItems : modelExportItems),
        ],
      },

      ...(canCopyModel && isModelMode && !scenarioMode
        ? [
            {
              text: 'Copy to project',
              icon: <ClipboardExportIcon fill="currentColor" />,
              onClick: onCopyToProject,
            },
          ]
        : []),
    ],
    [
      handleCloseDrawer,
      isDashboardMode,
      isModelMode,
      isRiskRegisterMode,
      scenarioMode,
      activeNode,
      insightsTab,
    ],
  );
};

export default useAdditionalMenuItems;
